import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, AnimeCharacter } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CommentSection from './components/CommentSection';
import RatingForm from '../rate/components/RatingForm';
import SEO from '../../components/SEO';

export default function CharacterPage() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const [character, setCharacter] = useState<AnimeCharacter | null>(null);
  const [loading, setLoading] = useState(true);
  const [existingRating, setExistingRating] = useState<number | undefined>(undefined);
  const [isUpdate, setIsUpdate] = useState(false);
  const [relatedCharacters, setRelatedCharacters] = useState<AnimeCharacter[]>([]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    if (slug) {
      loadCharacter();
    }
  }, [slug]);

  useEffect(() => {
    if (character && user) {
      checkExistingRating();
    }
  }, [character, user]);

  useEffect(() => {
    if (character) {
      loadRelatedCharacters();
    }
  }, [character]);

  const loadCharacter = async () => {
    // Extract ID from slug - ID is everything after the last occurrence of a pattern like "name-"
    // The slug format is: anime-name-character-name-UUID
    // We need to extract the UUID which is the last 36 characters (UUID format: 8-4-4-4-12)
    if (!slug) return;
    
    // Find the UUID pattern at the end of the slug
    const uuidPattern = /([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/i;
    const match = slug.match(uuidPattern);
    
    if (!match) {
      setLoading(false);
      return;
    }
    
    const id = match[1];
    
    const { data } = await supabase
      .from('anime_characters')
      .select('*')
      .eq('id', id)
      .single();

    if (data) {
      setCharacter(data);
    }
    setLoading(false);
  };

  const loadRelatedCharacters = async () => {
    if (!character) return;

    // Get random characters from the same series, excluding the current character
    const { data } = await supabase
      .from('anime_characters')
      .select('*')
      .eq('anime_name', character.anime_name)
      .neq('id', character.id)
      .limit(100);

    if (data && data.length > 0) {
      // Shuffle and take 6 random characters
      const shuffled = data.sort(() => Math.random() - 0.5);
      setRelatedCharacters(shuffled.slice(0, 6));
    }
  };

  const checkExistingRating = async () => {
    if (!character || !user) return;

    const { data } = await supabase
      .from('ratings')
      .select('rating')
      .eq('character_id', character.id)
      .eq('user_id', user.id)
      .single();

    if (data) {
      setExistingRating(data.rating);
      setIsUpdate(true);
    } else {
      setExistingRating(undefined);
      setIsUpdate(false);
    }
  };

  const handleRate = async (rating: number) => {
    if (!user || !character) return;

    if (isUpdate) {
      // Update existing rating
      const { error } = await supabase
        .from('ratings')
        .update({ rating, updated_at: new Date().toISOString() })
        .eq('character_id', character.id)
        .eq('user_id', user.id);

      if (!error) {
        setExistingRating(rating);
        // Reload character to get updated average
        loadCharacter();
      }
    } else {
      // Insert new rating
      const { error } = await supabase
        .from('ratings')
        .insert({
          character_id: character.id,
          user_id: user.id,
          rating,
        });

      if (!error) {
        setExistingRating(rating);
        setIsUpdate(true);
        // Reload character to get updated average
        loadCharacter();
      }
    }
  };

  const handleSkip = () => {
    // Do nothing on character detail page
  };

  // Helper function to create series URL slug
  const createSeriesSlug = (animeName: string) => {
    return encodeURIComponent(animeName);
  };

  // Helper function to create character URL slug
  const createCharacterSlug = (char: AnimeCharacter) => {
    const animePart = char.anime_name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const namePart = char.character_name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    return `${animePart}-${namePart}-${char.id}`;
  };

  const jsonLd = character ? {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: character.character_name,
    image: character.image_url,
    description: `${character.character_name} from ${character.anime_name}. Rate this anime character and see community ratings.`,
    gender: character.gender === 'boy' ? 'Male' : character.gender === 'girl' ? 'Female' : 'Neutral'
  } : undefined;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!character) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
        <Navbar />
        <div className="pt-24 pb-16 px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Character not found</h1>
          <Link
            to="/rate"
            className="inline-block px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap"
          >
            Back to Rating
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <SEO
        title={`${character.character_name} from ${character.anime_name} - Rate & Review`}
        description={`Rate ${character.character_name} from ${character.anime_name}. Current rating: ${character.average_rating.toFixed(1)}/10 based on ${character.total_ratings} ratings. Join the community and share your opinion!`}
        keywords={`${character.character_name}, ${character.anime_name}, anime character rating, ${character.character_name} rating, rate ${character.character_name}, anime character review`}
        canonical={`${siteUrl}/character/${slug}`}
        ogType="profile"
        ogImage={character.image_url}
        jsonLd={jsonLd}
      />
      
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          <Link
            to={`/series/${createSeriesSlug(character.anime_name)}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-pink-500 transition-colors cursor-pointer mb-4"
          >
            <i className="ri-arrow-left-line"></i>
            Back to {character.anime_name}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div>
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="aspect-[3/4] relative">
                  <img
                    src={character.image_url}
                    alt={`${character.character_name} - ${character.anime_name}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-3">
                {character.character_name}
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                from {character.anime_name}
              </p>

              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-gradient-to-br from-pink-50 to-purple-50 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 rounded-full">
                      <i className="ri-heart-fill text-xl text-white"></i>
                    </div>
                    <span className="text-sm font-medium text-gray-600">Average Rating</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">
                    {character.average_rating.toFixed(2)}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-pink-50 to-purple-50 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 rounded-full">
                      <i className="ri-user-line text-xl text-white"></i>
                    </div>
                    <span className="text-sm font-medium text-gray-600">Total Ratings</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">
                    {character.total_ratings}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-8">
                <div className={`w-10 h-10 flex items-center justify-center rounded-full ${
                  character.gender === 'girl' ? 'bg-pink-100' : 
                  character.gender === 'boy' ? 'bg-blue-100' : 'bg-purple-100'
                }`}>
                  <i className={`ri-${
                    character.gender === 'girl' ? 'women' : 
                    character.gender === 'boy' ? 'men' : 'genderless'
                  }-line text-xl ${
                    character.gender === 'girl' ? 'text-pink-500' : 
                    character.gender === 'boy' ? 'text-blue-500' : 'text-purple-500'
                  }`}></i>
                </div>
                <span className="text-base text-gray-700 capitalize">
                  {character.gender === 'boy' ? 'Male' : character.gender === 'girl' ? 'Female' : 'Neutral'}
                </span>
              </div>

              {user && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {isUpdate ? 'Update Your Rating' : 'Rate This Character'}
                  </h3>
                  <RatingForm
                    onRate={handleRate}
                    onSkip={handleSkip}
                    existingRating={existingRating}
                    isUpdate={isUpdate}
                    characterName={character.character_name}
                    animeName={character.anime_name}
                    isCharacterPage={true}
                  />
                </div>
              )}

              <Link
                to="/rate"
                className="block w-full py-4 text-center text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap"
              >
                Rate More Characters
              </Link>
            </div>
          </div>

          {character.description && character.description.trim() && (
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">About {character.character_name}</h2>
              <div 
                className="prose prose-sm max-w-none text-gray-700 character-description-content"
                dangerouslySetInnerHTML={{ __html: character.description }}
                style={{
                  lineHeight: '1.8'
                }}
              />
            </div>
          )}

          {relatedCharacters.length > 0 && (
            <div className="bg-white rounded-2xl shadow-xl p-6 mb-12">
              <h2 className="text-xl font-bold text-gray-900 mb-4">More from {character.anime_name}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                {relatedCharacters.map((char) => (
                  <Link
                    key={char.id}
                    to={`/character/${createCharacterSlug(char)}`}
                    className="group cursor-pointer"
                  >
                    <div className="aspect-[3/4] rounded-lg overflow-hidden mb-2 bg-gray-100">
                      <img
                        src={char.image_url}
                        alt={char.character_name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="text-xs font-semibold text-gray-900 line-clamp-2 group-hover:text-pink-500 transition-colors">
                      {char.character_name}
                    </h3>
                    <div className="flex items-center gap-1 mt-1">
                      <i className="ri-heart-fill text-xs text-pink-500"></i>
                      <span className="text-xs text-gray-600">{char.average_rating.toFixed(1)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Comments</h2>
            <CommentSection characterId={character.id} />
          </div>
        </div>
      </div>

      <Footer />
      <style>{`
        .character-description-content p {
          margin-bottom: 1.5em;
        }
        .character-description-content p:last-child {
          margin-bottom: 0;
        }
        .character-description-content ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin: 1rem 0;
        }
        .character-description-content ul li {
          margin-bottom: 0.5rem;
        }
        .character-description-content ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin: 1rem 0;
        }
        .character-description-content ol li {
          margin-bottom: 0.5rem;
        }
      `}</style>
    </div>
  );
}
